import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Skeleton, Typography } from "@mui/material";
import { NewReleasesOutlined } from "@mui/icons-material";
import color from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { formatDate } from "../../Helpers/dateHelpers";
import { useFeatureCatalog } from "../../Query/useFeatureCatalog";
import { ReleaseNote } from "../../Models/releaseNote";
import { ReleaseNoteDetailModal } from "../ReleaseNotes/ReleaseNoteDetailModal";
import DashboardCard, { CardLinkButton, EmptyState } from "./DashboardCard";
import { useDashboardColors } from "./dashboardColors";

const MAX_NOTES = 3;

const ReleaseNotesCard: React.FC = () => {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();
  const { accentIcon, accentSoft, accentBorder } = useDashboardColors();
  const { data, isLoading } = useFeatureCatalog();
  const [selected, setSelected] = useState<ReleaseNote | null>(null);

  const releaseNotes = useMemo<ReleaseNote[]>(() => {
    return [...(data?.release_notes ?? [])]
      .filter((note) => note.audience !== "internal")
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map((note) => ({
        id: note.pr,
        title: note.title,
        category: note.category,
        created_at: note.date,
        description: note.description,
      })) as ReleaseNote[];
  }, [data]);

  const latest = releaseNotes.slice(0, MAX_NOTES);

  return (
    <DashboardCard
      title={t("hub.news.title")}
      action={
        releaseNotes.length > 0 && (
          <CardLinkButton
            onClick={() =>
              navigate("/features_catalog", {
                state: { tab: "releaseNotes" },
              })
            }
          >
            {t("hub.news.showAll")}
          </CardLinkButton>
        )
      }
    >
      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[0, 1].map((key) => (
            <Skeleton key={key} variant="rounded" height={52} />
          ))}
        </Box>
      ) : latest.length === 0 ? (
        <EmptyState>{t("hub.news.empty")}</EmptyState>
      ) : (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, mx: -1 }}>
          {latest.map((note) => {
            return (
              <Box component="li" key={note.id}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSelected(note)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: 1,
                    py: 1,
                    border: 0,
                    borderRadius: "10px",
                    background: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "inherit",
                    "&:hover": {
                      backgroundColor: accentSoft,
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${workspace.primary_dark}`,
                      outlineOffset: -2,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: accentIcon,
                      backgroundColor: accentSoft,
                      border: `1px solid ${accentBorder}`,
                    }}
                  >
                    <NewReleasesOutlined sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "20px",
                        fontWeight: 600,
                        color: color.gray900,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {note.title}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 12,
                        lineHeight: "16px",
                        color: color.gray500,
                      }}
                    >
                      {[note.category || null, formatDate(note.created_at)]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      <ReleaseNoteDetailModal
        open={selected !== null}
        note={selected}
        onClose={() => setSelected(null)}
      />
    </DashboardCard>
  );
};

export default ReleaseNotesCard;
